/* eslint-disable security/detect-object-injection */
import React, { useCallback, useContext, useEffect } from 'react';
import _ from 'lodash';
import { DatatableUtil } from '@xengage/gw-portals-util-js';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { UWIssuesUtil } from "premier-policycommon-util-js";
import { useTranslator } from '@jutro/locale';
import { PremierConstants } from 'premier-constant-service';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import PrintDocumentsComponent from "premier-capability-policyjob-react/components/PrintDocumentsComponent/PrintDocumentsComponent";
import FormsComponent from "premier-capability-policyjob-react/components/FormComponent/FormComponent";
import messages from './QuotePage.messages';
import presentationMetadata from './QuotePage.metadata.json5';
import styles from './QuotePage.module.scss';
import CoveragesPage from '../Coverages/CoveragesPage';
import QuoteValidityComponent from '../../components/QuoteValidityComponent/QuoteValidityComponent';
import QuoteDetailsComponent from '../../components/QuoteDetailsComponent/QuoteDetailsComponent';
import { TextInput } from '@jutro/components';
import { BreakpointTrackerContext } from '@jutro/legacy/layout';

function QuotePage(props) {
    const {
        wizardData: submissionVM,
        steps,
        jumpTo,
        wizardData,
        updateWizardData,
        underwritingIssues
    } = props;
    const productCode = _.get(wizardData.value, 'baseData.productCode');
    const { lineName } = PremierConstants;
    const lobLineName = productCode ? lineName[productCode] : '';
    const coverableName = _.get(submissionVM.value, `baseData.productCode`) === 'RecMarProduct' ? 'marinePleasurecraft' :'droneCoverable';
    const subConName = _.get(submissionVM.value, `baseData.productCode`) === 'RecMarProduct' ? 'prmsubConofQuoExps' : 'psrsubofQuoExp6s';
    const addConName = _.get(submissionVM.value, `baseData.productCode`) === 'RecMarProduct' ? 'prmaddConofPolExps' : 'psraddConofPolExp6s';
    const propertyCovsName = 'psrcoverages';
    const { authHeader } = useAuthentication();

    const lobLineData = _.get(submissionVM.value, `lobData.${lobLineName}`);
    const coveragesPath = `lobData.${lobLineName}.lineCoverages`;

    const breakpoint = useContext(BreakpointTrackerContext);
    const isQuoteBlocked = UWIssuesUtil.isQuoteBlocked(underwritingIssues);
    const findBlockingPoint = UWIssuesUtil.findBlockingPoint(underwritingIssues);
    const translator = useTranslator();
    const metadata = presentationMetadata.quote;
    const periodStartDate = _.get(submissionVM.value, 'baseData.periodStartDate');

    const handleNext = useCallback(() => {
        const offeredQuotes = _.get(submissionVM.value, 'quoteData.offeredQuotes[0].publicID');

        _.set(submissionVM, 'bindData.chosenQuote', offeredQuotes);

        return submissionVM;
    }, [submissionVM]);

    const navigateToCoverages = useCallback(() => {

        const indexOfGeneralCoverages = _.findIndex(
            steps,
            ({ path }) => path === '/liabilityCoverages'
        );

        jumpTo(indexOfGeneralCoverages);
    }, [jumpTo, steps]);

    // eslint-disable-next-line arrow-body-style
    const getCell = (item, _index, property) => {
        return item[property.id];
    };

    const getFormTableData = () => _.map(lobLineData.lineCoverages.forms_Ext, (form) => ({
        formDes: form.formDes,
        formCategory: form.formCategory
    }));

    const getInternalUseOnlyConditionsOfQuoteTableData = () => {
        const internalUseOnlySubConData = _.get(lobLineData, `coverables.${coverableName}.internalUseOnly.${subConName}`)

        return _.map(internalUseOnlySubConData, (plesubofQuoExp) => ({
            conFieConditionsOfQuote: plesubofQuoExp
        }))
    };

    const getInternalUseOnlyAdditionalConditionsTableData = () => {
        const internalUseOnlyAddConData = _.get(lobLineData, `coverables.${coverableName}.internalUseOnly.${addConName}`)

        return _.map(internalUseOnlyAddConData, (pleaddConofPolExp) => ({
            conFieAdditionalConditions: pleaddConofPolExp
        }))
    }

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            navigateToCoverages,
            getCell,
            sortString: DatatableUtil.sortString,
        },
        resolveComponentMap: {
            CoveragesPageComponent: CoveragesPage,
            quoteDetailsComponent: QuoteDetailsComponent,
            quoteValidityComponent: QuoteValidityComponent,
            PrintDocumentsComponent,
            FormsComponent,
            TextInput
        }
    };

    const generatelocRiskseOverrides = () => {

        if (lobLineName === 'recMarLine') {
            return {};
        }

        const overrides = lobLineData.coverables.locRisks.map((locRisk, index) => ({
            [`locRiskSection${index}`]: {
                title: !_.isUndefined(locRisk?.accountLocFie?.displayName) ? locRisk?.accountLocFie?.displayName
                : _.get(submissionVM.value, 'baseData.policyAddress.displayName')
            },
            [`locRiskCoverageInfoComponent${index}`]: {
                wizardData,
                updateWizardData,
                clausesBasePath: `lobData.${lobLineName}.lineCoverages.locRiskCoverages.${propertyCovsName}.children[${index}].coverages`,
                title: "Coverages",
                clasueType: 'coverages',
                isEditable: false
            }
        }));

        return Object.assign({}, ...overrides);
    };

    const generateBotRiskseOverrides = () => {

        if (lobLineName !== 'recMarLine') {
            return {};
        }

        const overrides = lobLineData.coverables.boats.map((locRisk, index) => ({
            [`boaRiskSection${index}`]: {
                title: 'Boat'
            },
            [`boaRiskCoverageInfoComponent${index}`]: {
                wizardData,
                updateWizardData,
                clausesBasePath: `lobData.${lobLineName}.lineCoverages.boaRisCoverages.coverages.children[${index}].coverages`,
                title: "Coverages",
                clasueType: 'coverages',
                isEditable: false
            }
        }));

        return Object.assign({}, ...overrides);
    };

    const checkInternalUseVisibility = () => {
        const internalUseOnlySubConData = _.get(lobLineData, `coverables.${coverableName}.internalUseOnly.${subConName}`)
        const internalUseOnlyAddConData = _.get(lobLineData, `coverables.${coverableName}.internalUseOnly.${addConName}`)

        if (lobLineName === 'recMarLine') {
            return false
        }

        if (internalUseOnlySubConData.length > 0 || internalUseOnlyAddConData.length > 0) {
            return true
        }

        return false

    };

    const checkInternalUseSubConVisibility = () => {
        const internalUseOnlySubConData = _.get(lobLineData, `coverables.${coverableName}.internalUseOnly.${subConName}`)

        if (!_.isUndefined(internalUseOnlySubConData) && internalUseOnlySubConData.length > 0) {
            return true
        }

        return false

    };

    const checkInternalUseAddConVisibility = () => {
        const internalUseOnlyAddConData = _.get(lobLineData, `coverables.${coverableName}.internalUseOnly.${addConName}`)

        if (!_.isUndefined(internalUseOnlyAddConData) && internalUseOnlyAddConData.length > 0) {
            return true
        }

        return false

    };

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
        },
        coveragespagecomponent: {
            pathUpdated: coveragesPath
        },
        quoteDetailsComponent: {
            wizardData
        },
        quoteValidityComponent: {
            wizardData
        },
        underWritingIssues: {
            underwritingIssues,
            blockQuote: findBlockingPoint,
            filterUWIssuesInCustomOffering: false,
            quoteID: _.get(submissionVM.value, 'quoteID'),
            accountNumber: _.get(submissionVM.value, 'baseData.accountNumber')
        },
        liabilityCoverageInfoComponent: {
            wizardData,
            updateWizardData,
            clausesBasePath: `lobData.${lobLineName}.lineCoverages.coverages`,
            title: "Coverages",
            clasueType: 'coverages',
            isEditable: false
        },
        policyQuoteValidityDays: {
            content: _.get(submissionVM, `baseData.quoteValidityDays_Ext.value`)
        },
        policyQuoteValDaysHead: {
            content: translator(messages.plQuoteValidityDays, {
                quoteValidityDays: _.get(submissionVM, `baseData.quoteValidityDays_Ext.value`)
            })
        },
        tableFormSummary: {
            data: getFormTableData()
        },
        tableInternalUseOnlyConditionsOfQuote: {
            data: getInternalUseOnlyConditionsOfQuoteTableData()
        },
        tableInternalUseOnlyAdditionalConditions: {
            data: getInternalUseOnlyAdditionalConditionsTableData()
        },
        internalUseOnlyAccordion: {
            visible: checkInternalUseVisibility()
        },
        internalUseOnlyConditionsOfQuoteContainer: {
            visible: checkInternalUseSubConVisibility()
        },
        internalUseOnlyAdditionalConditionsContainer: {
            visible: checkInternalUseAddConVisibility()
        },
        liabilityAccordion: {
            visible: _.get(submissionVM, `lobData.${lobLineName}.lineCoverages.coverages`).length > 0
        },
        taxes: {
            visible: _.get(submissionVM.value, 'baseData.baseState_Ext') === 'QC' && !_.isUndefined(submissionVM.value.baseData.isApplicableForQCTax) && submissionVM.value.baseData.isApplicableForQCTax,
        },
        fees: {
            visible: _.get(submissionVM.value, 'quoteData.offeredQuotes') &&
                _.get(submissionVM.value, 'quoteData.offeredQuotes[0].premium.fees.amount') !== 0
        },
        policyfees: {
            visible: _.get(submissionVM.value, 'baseData.policyFee_Ext.amount') !== 0
        },
        inspectionFee: {
            visible: _.get(submissionVM.value, 'baseData.inspectionFee_Ext.amount') !== 0
        },
        extensionFee: {
            visible: _.get(submissionVM.value, 'baseData.extensionFee_Ext.amount') !== 0
        },
        PrintDocumentsComponent: {
            visible: submissionVM.value.baseData.periodStatus === 'Quoted',
            jobNumber: submissionVM.value.quoteID,
            authHeader
        },
        FormsComponent: {
            forms: lobLineData.lineCoverages.forms_Ext
        },
        startDate: {
            value: new Date(periodStartDate.year, periodStartDate.month, periodStartDate.day).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: '2-digit' })
        },
        ...generateBotRiskseOverrides(),
        ...generatelocRiskseOverrides()
    };

    useEffect(() => { }, []);

    return (
        <WizardPage
            disableNext={isQuoteBlocked}
            disableSave={isQuoteBlocked}
            onNext={handleNext}
            onSave={handleNext}
        >
            <ViewModelForm
                uiProps={metadata.content}
                model={submissionVM}
                overrideProps={overrideProps}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                componentMap={resolvers.resolveComponentMap}
            />
        </WizardPage>
    );
}

QuotePage.propTypes = wizardProps;
export default QuotePage;